import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Target, ArrowRight } from 'lucide-react';

interface PathCardProps {
  id: string;
  title: string;
  description: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  estimatedDuration: number;
  modulesCount: number;
  completionCriteria: string[];
}

export const PathCard = memo(function PathCard({
  id,
  title,
  description,
  level,
  estimatedDuration,
  modulesCount,
  completionCriteria
}: PathCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            level === 'beginner' ? 'bg-green-100 text-green-800' :
            level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            {level.charAt(0).toUpperCase() + level.slice(1)}
          </span>
          <div className="flex items-center text-gray-500 text-sm">
            <BookOpen className="h-4 w-4 mr-1" />
            {Math.ceil(estimatedDuration / 60)}h
          </div>
        </div>

        <h3 className="text-xl font-semibold mb-2">{title}</h3>
        <p className="text-gray-600 mb-4">{description}</p>

        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-emerald-600" />
            <span className="text-sm text-gray-700">{modulesCount} modules</span>
          </div>
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-emerald-600" />
            <span className="text-sm text-gray-700">
              {completionCriteria.length} completion criteria
            </span>
          </div>
        </div>

        <div className="border-t pt-4">
          <h4 className="font-medium text-gray-900 mb-2">What You'll Learn:</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            {completionCriteria.slice(0, 3).map((criteria, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-emerald-600 mt-1">•</span>
                {criteria}
              </li>
            ))}
          </ul>
        </div>

        <Link
          to={`/education/path/${id}`}
          className="mt-6 w-full bg-emerald-600 text-white py-3 px-4 rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center justify-center gap-2"
        >
          Start Learning Path
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
});
